import { inject, Injectable } from '@angular/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LoggerService } from './logger.service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../clientapp/src/environments/environment';
import { SaveDeviceInfoResponseDto } from '../../clientapp/src/models/server-models/reseponse-dtos/save-device-info-response-dto';
import { SaveDeviceInfoRequestDto } from '../../clientapp/src/models/server-models/request-dtos/save-device-info-request-dto';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { ToastService } from '../../clientapp/src/services/toast.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  httpClient = inject(HttpClient);
  loggerService = inject(LoggerService);
  toastService = inject(ToastService);

  saveDeviceInfo(fcmToken: string, platform: string, deviceId: string): Observable<SaveDeviceInfoResponseDto> {
    const requestDto: SaveDeviceInfoRequestDto = {
      fcmToken,
      deviceId,
      platform,
    };
    return this.httpClient.post<SaveDeviceInfoResponseDto>(`${environment.apiUrl}/api/device-info`, requestDto);
  }

  constructor() {
    FirebaseMessaging.addListener('notificationReceived', (event) => {
      this.toastService.info(event.notification.title ?? 'New message');
    });
  }

  async ensureNotificationsAllowed(): Promise<boolean> {
    try {
      if (!await FirebaseMessaging.isSupported()) {
        this.loggerService.logEvent('Notifications_not_supported_on_this_device');
        return false;
      }

      const result = await FirebaseMessaging.requestPermissions();

      const isGranted = result.receive == 'granted';

      if (isGranted) {
        this.loggerService.logEvent('User_allowed_push_notifications');
      } else {
        this.loggerService.logEvent('User_denied_push_notifications', {
          receive: result.receive,
        });
      }

      const token = await this.getToken();

      if (!token) {
        return false;
      }

      const deviceId = await this.getDeviceId();
      const platform = Capacitor.getPlatform();

      this.saveDeviceInfo(token, platform, deviceId).subscribe({
        next: (_) => {
          this.loggerService.logEvent('Device_info_saved', {
            platform,
            deviceId: deviceId,
          });
        },
        error: async (error) => {
          this.loggerService.logException(error, 'Failed to save device info with HTTP call');
        },
      });

      return isGranted;
    } catch (ex) {
      this.loggerService.logException(ex, 'Failed notifications setup');
    }

    return false;
  }

  async getToken(): Promise<string | null> {
    try {
      return (
        await FirebaseMessaging.getToken({
          vapidKey: environment.firebaseConfig.vapidKey,
        })
      ).token;
    } catch (ex) {
      this.loggerService.logException(ex, 'Failed to get FCM token');
      return null;
    }
  }

  async getDeviceId() {
    try {
      return (await Device.getId()).identifier;
    } catch (ex) {
      this.loggerService.logException(ex, 'Failed to get device id, when setting FCM');
      return '';
    }
  }
}
